import { useState } from "react";
import { Users, Trophy, ChevronDown, LayoutGrid, Film, Images, Image as ImageIcon, Sparkles, Zap } from "lucide-react";
import { DonutSection } from "./DonutSection";

export const fmt = (n) => Number(n || 0).toLocaleString();
export const fmtK = (n) => (n >= 10000 ? `${Math.round(n / 1000)}K` : fmt(n));
export const pctLabel = (p) => `${p < 1 || p > 99 ? p.toFixed(1) : p.toFixed(0)}%`;

export const Panel = ({ title, icon: Icon, children, testId, className = "" }) => (
  <div className={`bg-white/5 rounded-2xl p-4 md:p-5 border border-white/10 ${className}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-sky-300" />
      <p className="text-white font-semibold">{title}</p>
    </div>
    {children}
  </div>
);

export const StoryBox = ({ children, testId }) => (
  <div className="bg-gradient-to-r from-sky-500/15 to-indigo-500/10 border border-sky-400/20 rounded-2xl p-5 mb-5" data-testid={testId}>
    <p className="text-slate-100 text-base md:text-lg leading-relaxed">{children}</p>
  </div>
);

const Tile = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-sky-500/10 border border-sky-400/20 rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5 text-sky-300" />
      <p className="text-[10px] uppercase tracking-[0.14em] text-sky-200/80 font-bold">{label}</p>
    </div>
    <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">{value}</p>
    {sub && <p className="text-slate-300 text-xs mt-1">{sub}</p>}
  </div>
);

const Gauge = ({ ctr, benchmark, label, testId }) => {
  const mult = benchmark > 0 ? ctr / benchmark : 0;
  const above = mult >= 1;
  const fill = Math.min(mult / 5, 1);
  const half = Math.PI * 52;
  const color = above ? "#a3e635" : "#f59e0b";
  return (
    <div className={`h-full rounded-xl p-4 border flex flex-col items-center justify-center ${above ? "bg-lime-400/5 border-lime-400/25" : "bg-amber-400/5 border-amber-400/25"}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold mb-1 text-center">{label}</p>
      <svg className="w-28 h-16" viewBox="0 0 130 75">
        <path d="M 10 70 A 52 52 0 0 1 120 70" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" strokeLinecap="round" />
        <path d="M 10 70 A 52 52 0 0 1 120 70" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${fill * half} ${half}`} />
        <line x1="65" y1="70" x2={65 + 38 * Math.cos(Math.PI + fill * Math.PI)} y2={70 + 38 * Math.sin(Math.PI + fill * Math.PI)} stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="65" cy="70" r="3" fill="white" />
      </svg>
      <p className={`text-2xl font-bold tabular-nums ${above ? "text-lime-300" : "text-amber-300"}`}>{mult.toFixed(1)}x</p>
      <p className="text-slate-400 text-[11px]">Typical click rate: {benchmark}%</p>
    </div>
  );
};

// tiles: [{icon,label,value,sub,testId}], gauge: {ctr, benchmark, label, testId}
export const HeadlineTiles = ({ tiles, gauge }) => (
  <div className={`grid grid-cols-2 ${tiles.length >= 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-6`}>
    {tiles.map((t) => <Tile key={t.testId} {...t} />)}
    <div className="col-span-2 lg:col-span-1"><Gauge {...gauge} /></div>
  </div>
);

const BAR_COLORS = ["from-violet-500 to-violet-400", "from-sky-500 to-blue-500", "from-teal-500 to-emerald-400"];
const TEXT_COLORS = ["text-violet-300", "text-sky-300", "text-teal-300"];

// Lime callout when returning people click at more than double the new-people rate.
export const RetargetingCallout = ({ back, fresh, testId }) => {
  if (!back || !fresh || !(back.impressions > 0) || !(fresh.ctr > 0) || back.ctr <= fresh.ctr * 2) return null;
  const x = back.ctr / fresh.ctr;
  return (
    <div className="flex items-start gap-3 rounded-xl p-4 mb-4 bg-lime-400/10 border border-lime-400/40" data-testid={testId}>
      <Zap className="w-5 h-5 text-lime-300 flex-shrink-0 mt-0.5" />
      <p className="text-lime-100 text-sm leading-relaxed">
        <span className="text-lime-300 font-bold">Returning people clicked at {x.toFixed(1)}x the rate of new people</span> ({back.ctr}% vs {fresh.ctr}%).
        Every new person who sees your ads today becomes a warmer, more likely customer the next time they see you.
      </p>
    </div>
  );
};

const DOT_COLORS = ["bg-violet-400", "bg-sky-400", "bg-teal-400"];

// groups: [{key, label, desc, icon, impressions, clicks, ctr, pct, extra}]
export const WhoSawIt = ({ groups, story, testPrefix, back, fresh }) => {
  const best = Math.max(...groups.map((g) => g.ctr));
  return (
    <Panel title="Who saw it" icon={Users} testId={`${testPrefix}-who-saw-it`} className="mb-6">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-800 mb-3" data-testid={`${testPrefix}-audience-bar`}>
        {groups.map((g, i) => (
          <div key={g.key} className={`bg-gradient-to-r ${BAR_COLORS[i % 3]} ${i === groups.length - 1 ? "flex-1" : ""}`}
            style={i === groups.length - 1 ? undefined : { width: `${g.impressions > 0 ? Math.max(g.pct, 2) : 0}%` }} />
        ))}
      </div>
      <div className="divide-y divide-white/5 mb-3">
        {groups.map((g, i) => (
          <div key={g.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={`${testPrefix}-audience-${g.key}`} title={g.desc}>
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT_COLORS[i % 3]}`} />
            <p className={`font-semibold text-sm ${TEXT_COLORS[i % 3]}`}>{g.label}</p>
            <span className="text-slate-400 text-xs">{pctLabel(g.pct)} of views</span>
            {groups.length > 1 && g.ctr === best && g.impressions > 0 && <span className="whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-bold bg-lime-400/15 text-lime-300 border border-lime-400/30">Best click rate</span>}
            <p className="ml-auto text-slate-300 text-xs tabular-nums">
              <span className="text-white font-semibold">{fmtK(g.impressions)}</span> views · <span className="text-white font-semibold">{fmt(g.clicks)}</span> clicks · <span className="text-white font-semibold">{g.ctr}%</span>
              {g.extra && <span className="text-teal-300 font-semibold"> · {g.extra}</span>}
            </p>
          </div>
        ))}
      </div>
      <RetargetingCallout back={back} fresh={fresh} testId={`${testPrefix}-retargeting-callout`} />
      {story && !(back && fresh && back.impressions > 0 && fresh.ctr > 0 && back.ctr > fresh.ctr * 2) && <p className="text-slate-300 text-sm leading-relaxed" data-testid={`${testPrefix}-audience-story`}>{story}</p>}
    </Panel>
  );
};

export const adFormat = (name = "") => {
  if (/\.gif|animated|scroller/i.test(name)) return "Animated";
  if (/\.(mp4|mov)|video|vertical/i.test(name)) return "Video";
  if (/carousel/i.test(name)) return "Carousel";
  return "Image";
};
const FORMAT_ICON = { Video: Film, Carousel: Images, Animated: Sparkles, Image: ImageIcon };

const AdCard = ({ ad, rank, CreativeIcon, campaignType, benchmark, testPrefix }) => {
  const Icon = FORMAT_ICON[ad.format] || ImageIcon;
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`${testPrefix}-best-ad-${rank}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/15 text-sky-200 border border-sky-400/25"><Icon className="w-3 h-3" />{ad.format}</span>
        <span className={`text-xs font-black ${rank === 1 ? "text-lime-300" : "text-slate-300"}`}>#{rank}</span>
      </div>
      <div className="flex items-start gap-2 mb-3 min-h-[2.5rem]">
        <p className="text-white text-sm font-medium leading-snug line-clamp-2 flex-1 break-all" title={ad.name}>{ad.name}</p>
        {CreativeIcon && <CreativeIcon campaignType={campaignType} adName={ad.name} />}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><p className="text-white font-bold text-sm tabular-nums">{fmtK(ad.impressions)}</p><p className="text-slate-400 text-[11px]">Seen</p></div>
        <div><p className="text-white font-bold text-sm tabular-nums">{fmt(ad.clicks)}</p><p className="text-slate-400 text-[11px]">Clicks</p></div>
        <div><p className={`font-bold text-sm tabular-nums ${ad.ctr >= benchmark ? "text-lime-300" : "text-white"}`}>{ad.ctr}%</p><p className="text-slate-400 text-[11px]">Click rate</p></div>
      </div>
    </div>
  );
};

export const rankAds = (list) => {
  const agg = {};
  list.forEach((c) => {
    const k = c.name || "Untitled ad";
    if (!agg[k]) agg[k] = { name: k, impressions: 0, clicks: 0 };
    agg[k].impressions += c.impressions || 0;
    agg[k].clicks += c.clicks || 0;
  });
  return Object.values(agg)
    .filter((c) => c.impressions > 0)
    .map((c) => ({ ...c, ctr: c.clicks > 0 ? Number(((c.clicks / c.impressions) * 100).toFixed(2)) : 0, format: adFormat(c.name) }))
    .sort((a, b) => b.impressions * b.ctr - a.impressions * a.ctr);
};

export const BestAds = ({ ads, CreativeIcon, campaignType, benchmark, testPrefix }) => {
  const [open, setOpen] = useState(false);
  const top = ads.slice(0, 3);
  const rest = ads.slice(3);
  return (
    <Panel title="Your best ads" icon={Trophy} testId={`${testPrefix}-best-ads`} className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {top.map((ad, i) => <AdCard key={ad.name} ad={ad} rank={i + 1} CreativeIcon={CreativeIcon} campaignType={campaignType} benchmark={benchmark} testPrefix={testPrefix} />)}
        {top.length === 1 && (
          <div className="md:col-span-2 flex items-center rounded-xl border border-dashed border-white/10 p-4" data-testid={`${testPrefix}-single-ad-note`}>
            <p className="text-slate-300 text-sm leading-relaxed">One ad carried the whole campaign this period, earning all {fmt(top[0].clicks)} of its clicks. Adding a second creative next time shows which message lands best.</p>
          </div>
        )}
      </div>
      {rest.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-sm text-sky-200 hover:text-white font-semibold" data-testid={`${testPrefix}-all-ads-toggle`}>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} /> {open ? "Hide" : "See"} all {ads.length} ads
          </button>
          {open && (
            <div className="mt-3 space-y-1.5" data-testid={`${testPrefix}-all-ads-list`}>
              {rest.map((ad) => (
                <div key={ad.name} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2 text-xs">
                  {CreativeIcon && <CreativeIcon campaignType={campaignType} adName={ad.name} />}
                  <span className="text-slate-200 truncate flex-1" title={ad.name}>{ad.name}</span>
                  <span className="text-slate-400 tabular-nums hidden sm:inline">{fmtK(ad.impressions)} seen</span>
                  <span className="text-slate-400 tabular-nums">{fmt(ad.clicks)} clicks</span>
                  <span className="text-white font-semibold tabular-nums w-12 text-right">{ad.ctr}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

const Bar = ({ label, pct, right, icon: Icon, highlight, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between text-xs mb-1 gap-2">
      <span className="flex items-center gap-1.5 text-slate-200 min-w-0">{Icon && <Icon className="w-3.5 h-3.5 text-sky-300 flex-shrink-0" />}<span className="truncate">{label}</span>
        {highlight && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-lime-400/15 text-lime-300 border border-lime-400/30 flex-shrink-0 whitespace-nowrap">Best click rate</span>}
      </span>
      <span className="text-white font-semibold tabular-nums flex-shrink-0">{right}</span>
    </div>
    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400" style={{ width: `${Math.max(pct, 2)}%` }} />
    </div>
  </div>
);

// sections: [{title, rows: [{label, pct, right, icon, highlight}], note, testId}]
export const WhereAndWhat = ({ sections, testId, donut = false }) => {
  const shown = sections.filter((s) => s.rows.length > 0);
  if (!shown.length) return null;
  return (
    <Panel title="Where and on what" icon={LayoutGrid} testId={testId}>
      <div className={`grid grid-cols-1 ${shown.length > 1 ? "lg:grid-cols-2" : ""} gap-6`}>
        {shown.map((s) => (
          <div key={s.title} className="space-y-3" data-testid={s.testId}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-bold">{s.title}</p>
            {donut
              ? <DonutSection rows={s.rows} testId={s.testId} />
              : s.rows.map((r, i) => <Bar key={r.label} {...r} testId={`${s.testId}-${i}`} />)}
            {s.note && <p className="text-slate-400 text-[11px]">{s.note}</p>}
          </div>
        ))}
      </div>
    </Panel>
  );
};

export const GenderSplit = ({ gender = [], testId }) => {
  const f = gender.find((g) => g.segment === "Female") || { impressions: 0, ctr: 0 };
  const m = gender.find((g) => g.segment === "Male") || { impressions: 0, ctr: 0 };
  const total = f.impressions + m.impressions;
  if (!total) return null;
  const fp = (f.impressions / total) * 100;
  const leader = f.ctr === m.ctr ? null : f.ctr > m.ctr ? "Women" : "Men";
  return (
    <Panel title="Women and men" icon={Users} testId={testId} className="mt-6">
      <div className="flex h-9 rounded-full overflow-hidden bg-slate-800 mb-3">
        <div className="bg-gradient-to-r from-pink-500 to-pink-400 flex items-center pl-3" style={{ width: `${fp}%` }}><span className="text-white text-xs font-bold">Women {fp.toFixed(0)}%</span></div>
        <div className="bg-gradient-to-r from-sky-500 to-blue-500 flex items-center justify-end pr-3 flex-1"><span className="text-white text-xs font-bold">Men {(100 - fp).toFixed(0)}%</span></div>
      </div>
      {leader && <p className="text-slate-300 text-sm">{leader} clicked more often ({Math.max(f.ctr, m.ctr)}% vs {Math.min(f.ctr, m.ctr)}%).</p>}
    </Panel>
  );
};
